/* eslint-disable no-magic-numbers */
import { AreaUnit, LengthUnit } from '@fulcrum/lib';
import { METRIC, INT_SURVEY, US_SURVEY } from './conversion-constants';
import { BaseConverterProps } from './base-converter-props.types';
import { UnreachableCaseError } from 'ts-essentials';

export const mapToMillimeters = ({
  value,
  baseUnit,
}: BaseConverterProps<number, LengthUnit>): number => {
  switch (baseUnit) {
    case 'mm':
      return value;
    case 'cm':
      return value * METRIC.SCALE.centiToMilli;
    case 'm':
      return value * METRIC.SCALE.baseToMilli;
    case 'km':
      return value * METRIC.SCALE.kiloToMilli;
    case 'in':
      return value * INT_SURVEY.LENGTH.inchesToMillimeters;
    case 'ft':
      return value * INT_SURVEY.LENGTH.feetToMillimeters;
    case 'yd':
      return value * INT_SURVEY.LENGTH.yardsToMillimeters;
    case 'mi':
      return value * INT_SURVEY.LENGTH.milesToMillimeters;
    case 'sIN':
      return value * US_SURVEY.LENGTH.inchesToMillimeters;
    case 'sFT':
      return value * US_SURVEY.LENGTH.feetToMillimeters;
    case 'sYD':
      return value * US_SURVEY.LENGTH.yardsToMillimeters;
    case 'sMI':
      return value * US_SURVEY.LENGTH.milesToMillimeters;
    default:
      throw new UnreachableCaseError(baseUnit);
  }
};
export const mapAreaToMeter = ({
  value,
  baseUnit,
}: BaseConverterProps<number, AreaUnit>): number | null => {
  switch (baseUnit) {
    case 'mm2':
      return value * METRIC.AREA.squareMilliToSquareMeter;
    case 'cm2':
      return value * METRIC.AREA.squareCentiToSquareMeter;
    case 'm2':
      return value;
    case 'km2':
      return value * METRIC.AREA.squareKiloToSquareMeter;
    case 'in2':
      return value * INT_SURVEY.AREA.squareInchesToSquareMeters;
    case 'ft2':
      return value * INT_SURVEY.AREA.squareFeetToSquareMeters;
    case 'yd2':
      return value * INT_SURVEY.AREA.squareYardsToSquareMeters;
    default:
      throw new UnreachableCaseError(baseUnit);
  }
};

export const convertLength = (
  value: number,
  currUnit: LengthUnit,
  destUnit: LengthUnit
): number => {
  const mmValue = mapToMillimeters({ value, baseUnit: currUnit });

  switch (destUnit) {
    case 'mm':
      return mmValue;
    case 'cm':
      return mmValue / METRIC.SCALE.centiToMilli;
    case 'm':
      return mmValue * METRIC.SCALE.milliToBase;
    case 'km':
      return mmValue / METRIC.SCALE.kiloToMilli;
    case 'in':
      return mmValue / INT_SURVEY.LENGTH.inchesToMillimeters;
    case 'ft':
      return mmValue / INT_SURVEY.LENGTH.feetToMillimeters;
    case 'yd':
      return mmValue / INT_SURVEY.LENGTH.yardsToMillimeters;
    case 'mi':
      return mmValue / INT_SURVEY.LENGTH.milesToMillimeters;
    case 'sIN':
      return mmValue / US_SURVEY.LENGTH.inchesToMillimeters;
    case 'sFT':
      return mmValue / US_SURVEY.LENGTH.feetToMillimeters;
    case 'sYD':
      return mmValue / US_SURVEY.LENGTH.yardsToMillimeters;
    case 'sMI':
      return mmValue / US_SURVEY.LENGTH.milesToMillimeters;
    default:
      throw new UnreachableCaseError(destUnit);
  }
};

export const mapMeterToArea = (
  value: number,
  unit: AreaUnit
): number | null => {
  switch (unit) {
    case 'mm2':
      return value / METRIC.AREA.squareMilliToSquareMeter;
    case 'cm2':
      return value / METRIC.AREA.squareCentiToSquareMeter;
    case 'm2':
      return value;
    case 'km2':
      return value / METRIC.AREA.squareKiloToSquareMeter;
    case 'in2':
      return value / INT_SURVEY.AREA.squareInchesToSquareMeters;
    case 'ft2':
      return value / INT_SURVEY.AREA.squareFeetToSquareMeters;
    case 'yd2':
      return value / INT_SURVEY.AREA.squareYardsToSquareMeters;
    default:
      throw new UnreachableCaseError(unit);
  }
};

export const convertFeetInchesToMeters = (
  feet: number,
  inches: number,
  fraction: number,
  lengthFractions: number
): number => {
  return (
    feet * (INT_SURVEY.LENGTH.feetToMillimeters / METRIC.SCALE.baseToMilli) +
    inches *
      (INT_SURVEY.LENGTH.inchesToMillimeters / METRIC.SCALE.baseToMilli) +
    (fraction *
      (INT_SURVEY.LENGTH.inchesToMillimeters / METRIC.SCALE.baseToMilli)) /
      lengthFractions
  );
};

export const convertMetersToFeetInches = (
  meters: number,
  lengthFractions: number
): { feet: number; inches: number; fraction: number } => {
  const isNegative = meters < 0;
  const totalInches = Math.abs(meters) * METRIC.LENGTH.metersToIntInches;
  const feetValue = Math.floor(totalInches / INT_SURVEY.SCALE.inchesToFeet);
  const remainingInches =
    totalInches - feetValue * INT_SURVEY.SCALE.inchesToFeet;
  const inchesValue = Math.floor(remainingInches);
  const fractionalInches = remainingInches - inchesValue;
  const fractionValue = Math.floor(fractionalInches * lengthFractions);

  return {
    feet: isNegative ? -feetValue : feetValue,
    inches: inchesValue,
    fraction: fractionValue,
  };
};

/**
 * Map of unit strings that can appear in billboard text to LengthUnit values.
 * Case-sensitive matching for proper unit identification.
 */
const UNIT_MAP: Record<string, LengthUnit> = {
  // Metric units
  m: 'm',
  mm: 'mm',
  cm: 'cm',
  km: 'km',
  // International/Imperial units
  in: 'in',
  ft: 'ft',
  mi: 'mi',
  yd: 'yd',
  // US Survey units
  sIN: 'sIN',
  sFT: 'sFT',
  sMI: 'sMI',
  sYD: 'sYD',
};

/**
 * Parses a chainage string (e.g., "1234.56 m", "500 ft", "100sFT") and converts the value to meters.
 * @param text The billboard text containing a chainage value and unit
 * @returns The chainage value in meters, or NaN if parsing fails
 */
export const parseChainageToMeters = (text: string): number => {
  if (!text) {
    return NaN;
  }

  // Match the ENTIRE string as a number (with optional decimal and negative sign)
  // followed by optional whitespace and a unit. The anchors (^ / $) ensure that
  // compound sentences such as station equation labels are NOT matched.
  // Survey units (sIN, sFT, sYD, sMI) are listed first to avoid partial matches.
  // Examples: "1234.56 m", "500ft", "-123.45 km", "100sFT"
  const match =
    // eslint-disable-next-line sonarjs/super-linear-regex -- input is a short unit-suffixed label matched by an anchored pattern
    /^(-?\d+\.?\d*)\s*(sIN|sFT|sYD|sMI|mm|cm|km|in|ft|mi|yd|m)$/.exec(
      text.trim()
    );

  if (!match) {
    return NaN;
  }

  const value = parseFloat(match[1]);
  if (isNaN(value)) {
    return NaN;
  }

  const unit = UNIT_MAP[match[2]];

  if (!unit) {
    return NaN;
  }

  if (unit === 'm') {
    return value;
  }

  // Convert to meters using the existing utility
  const meters = convertLength(value, unit, 'm');
  return meters ?? NaN;
};
